// The upload API only accepts apng/gif/jpeg/png/heif, so webp files are converted in the browser
// before upload. Converting client-side also keeps webp away from the publish pipeline.
const CONVERTIBLE_TYPES = ["image/webp"];

const JPEG_QUALITY = 0.92;

export const pickOutputFormat = (hasAlpha: boolean): { type: string; extension: string } =>
  hasAlpha ? { type: "image/png", extension: "png" } : { type: "image/jpeg", extension: "jpg" };

export const replaceExtension = (fileName: string, extension: string): string =>
  `${fileName.replace(/\.[^.]+$/, "")}.${extension}`;

export const hasAlphaPixels = (data: Uint8ClampedArray): boolean => {
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) {
      return true;
    }
  }
  return false;
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob | null> =>
  new Promise(resolve => canvas.toBlob(resolve, type, quality));

// Returns the original file untouched for formats the API accepts, or when the
// browser can't decode/encode, the server-side validation stays the safety net.
export const convertUnsupportedImage = async (file: File): Promise<File> => {
  if (!CONVERTIBLE_TYPES.includes(file.type)) {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement("canvas");
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const context = canvas.getContext("2d");
    if (!context) {
      return file;
    }
    context.drawImage(bitmap, 0, 0);

    const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
    const { type, extension } = pickOutputFormat(hasAlphaPixels(data));

    const blob = await canvasToBlob(canvas, type, type === "image/jpeg" ? JPEG_QUALITY : undefined);
    if (!blob) {
      return file;
    }

    return new File([blob], replaceExtension(file.name, extension), { type });
  } catch (err) {
    console.error("Failed to convert image for upload, uploading original", { fileName: file.name, err });
    return file;
  }
};
